import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { StatusBar, StatusSelect, CARRIER_STATUSES, carrierStatusColor, CARRIER_CHANGE_ROLES, pickupDeparted, finalDelivered } from './StatusBar';
import { SearchableSelect } from '../ui/searchable-select';
import { CarrierFormDialog } from '../CarrierFormDialog';

export const CarrierPanel = ({ load, carriers, onChange, onCarrierCreated }) => {
  const navigate = useNavigate();
  const [newCarrier, setNewCarrier] = useState(null); // {name|mc_number} prefill when opening the Add Carrier dialog
  const { user } = useAuth();
  const set = (field) => (e) => onChange(field, e.target.value);
  const carrier = carriers.find(c => c.id === load.carrier_id);
  const departed = pickupDeparted(load);
  const delivered = finalDelivered(load);
  const locked = departed || delivered;
  const elevated = CARRIER_CHANGE_ROLES.includes(user?.role);
  const statusOptions = CARRIER_STATUSES.filter(st => {
    if (st === 'Recovering') return !departed;
    if (st === 'Available') return !locked || elevated;
    return true;
  });
  const lockTitle = delivered ? 'Carrier assignment is locked once Delivered — corrections need elevated permission'
    : departed ? 'Driver departed the shipper — Recovering is blocked; carrier changes need elevated permission' : undefined;

  const handleStatusChange = (v) => {
    if (v === 'Recovering' && departed) { toast.error('Recovering is blocked once the driver has departed the shipper'); return; }
    if (['Setting Up', 'Covered', 'RC Sent', 'Verified', 'Change'].includes(v) && !load.carrier_id) {
      toast.error(`Assign a carrier before setting Carrier Status to "${v}"`); return;
    }
    onChange('carrier_status', v);
    if (v === 'Available' || v === 'Recovering') {
      ['carrier_id', 'carrier_name', 'dispatch_name', 'dispatch_phone', 'driver_name', 'driver_phone',
       'truck_number', 'truck_lp', 'truck_vin', 'trailer_number', 'trailer_lp'].forEach(f => onChange(f, ''));
    }
  };

  const handleCarrierSelect = (val, created) => {
    if (locked && load.carrier_id && !elevated) { toast.error('Replacing the carrier after pickup departure requires elevated permission'); return; }
    const c = created || carriers.find(x => x.id === val);
    onChange('carrier_id', val);
    if (c) onChange('carrier_name', c.name);
    if (val && (!load.carrier_status || ['Available', 'Recovering'].includes(load.carrier_status))) onChange('carrier_status', 'Setting Up');
  };

  const fmtWhen = (iso) => iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

  return (
    <div className="lw-panel" data-testid="lw-panel-carrier">
      <StatusBar
        left={<span title={lockTitle}>Carrier Assignment{locked ? ' 🔒' : ''}{(load.carrier_history || []).length > 0 ? <span style={{ fontWeight: 400, fontSize: 10, marginLeft: 6 }} title="Previous carriers kept in history" data-testid="lw-carrier-history-count">({load.carrier_history.length} prior)</span> : null}</span>}
        right={
          <StatusSelect
            value={load.carrier_status}
            options={statusOptions}
            onChange={handleStatusChange}
            disabled={delivered && !elevated}
            testId="lw-carrier-status-select"
            placeholder="Status..."
          />
        }
        color={carrierStatusColor(load.carrier_status)}
        testId="lw-statusbar-carrier"
      />

      <div className="lw-row">
        <span
          className="lw-link"
          style={{ width: 80 }}
          onClick={() => load.carrier_id && navigate(`/carriers/${load.carrier_id}`)}
        >Name</span>
        <SearchableSelect
          options={carriers.map(c => ({ value: c.id, label: c.name, description: [c.mc_number && `MC ${c.mc_number}`, c.dot_number && `DOT ${c.dot_number}`].filter(Boolean).join(' · '), searchTerms: `${c.mc_number || ''} ${c.dot_number || ''}` }))}
          value={load.carrier_id || ''}
          onValueChange={handleCarrierSelect}
          placeholder="Select carrier..."
          searchPlaceholder="Search name, MC # or DOT #"
          emptyText="No carrier matches"
          onCreate={(q) => setNewCarrier(/^\d+$/.test(q.trim()) ? { mc_number: q.trim() } : { name: q.trim() })}
          createLabel="Add new carrier"
          className="h-7 text-sm flex-1"
          data-testid="lw-carrier-select"
        />
        <CarrierFormDialog
          open={!!newCarrier}
          onOpenChange={(o) => !o && setNewCarrier(null)}
          initial={newCarrier || {}}
          onCreated={(c) => { onCarrierCreated?.(c); handleCarrierSelect(c.id, c); }}
        />
      </div>
      <div className="lw-row">
        <span className="lw-link" style={{ width: 80 }}>MC #</span>
        <input className="lw-input narrow" readOnly value={carrier?.mc_number || ''} data-testid="lw-carrier-mc" />
      </div>
      {(load.carrier_status === 'RC Sent' || load.rate_con_sent_at) && (
        <div className="lw-row" style={{ fontSize: 11 }}>
          <span className="lw-label w80">RC Sent</span>
          <span style={{ color: '#686868' }} data-testid="lw-rc-sent-at">{fmtWhen(load.rate_con_sent_at) || '—'}</span>
          <label style={{ marginLeft: 12, display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input type="checkbox" className="lw-checkbox" style={{ width: 13, height: 13 }} checked={!!load.rate_con_received_at}
              onChange={(e) => onChange('rate_con_received_at', e.target.checked ? new Date().toISOString() : null)} data-testid="lw-rc-received-checkbox" />
            Received by carrier{load.rate_con_received_at ? ` ${fmtWhen(load.rate_con_received_at)}` : ''}
          </label>
        </div>
      )}
      {load.carrier_verified_at && (
        <div className="lw-row" style={{ fontSize: 11, color: '#686868' }} data-testid="lw-verified-info">
          <span className="lw-label w80">Verified</span>
          <span>{fmtWhen(load.carrier_verified_at)}{load.carrier_verified_by_name ? ` by ${load.carrier_verified_by_name}` : ''}</span>
        </div>
      )}
      <div className="lw-row">
        <span className="lw-label w80">Dispatch</span>
        <input className="lw-input" value={load.dispatch_name || ''} onChange={set('dispatch_name')} data-testid="lw-dispatch-name" />
      </div>
      <div className="lw-row">
        <span className="lw-label w80">Phone</span>
        <input className="lw-input narrow" value={load.dispatch_phone || ''} onChange={set('dispatch_phone')} data-testid="lw-dispatch-phone" />
      </div>
      <div className="lw-row">
        <span className="lw-label w80">Driver(s)</span>
        <input className="lw-input" value={load.driver_name || ''} onChange={set('driver_name')} data-testid="lw-driver-name" />
      </div>
      <div className="lw-row">
        <span className="lw-label w80">Phone</span>
        <input className="lw-input narrow" value={load.driver_phone || ''} onChange={set('driver_phone')} data-testid="lw-driver-phone" />
      </div>

      <div className="lw-sep" />

      <div className="lw-row">
        <span className="lw-label w80">Truck</span>
        <input className="lw-input tiny" value={load.truck_number || ''} onChange={set('truck_number')} data-testid="lw-truck-number" />
        <span className="lw-label" style={{ marginLeft: 6 }}>Plate</span>
        <input className="lw-input tiny" value={load.truck_lp || ''} onChange={set('truck_lp')} data-testid="lw-truck-plate" />
      </div>
      <div className="lw-row">
        <span className="lw-label w80">VIN #</span>
        <input className="lw-input" value={load.truck_vin || ''} onChange={set('truck_vin')} data-testid="lw-truck-vin" />
      </div>
      <div className="lw-row">
        <span className="lw-label w80">Trailer</span>
        <input className="lw-input tiny" value={load.trailer_number || ''} onChange={set('trailer_number')} data-testid="lw-trailer-number" />
        <span className="lw-label" style={{ marginLeft: 6 }}>Plate</span>
        <input className="lw-input tiny" value={load.trailer_lp || ''} onChange={set('trailer_lp')} data-testid="lw-trailer-plate" />
      </div>
    </div>
  );
};
